import type { BlockKind, ScenicKind, TileKind } from '../types';

/** Development-only fallback art used when a generated PNG is unavailable. */
export function drawFallbackMario(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  facing: -1 | 1,
  pose: 'idle' | 'run' | 'jump' | 'fall' | 'death',
  frame: number,
): void {
  const px = Math.round(x);
  const py = Math.round(y);
  context.save();
  if (facing < 0) {
    context.translate(px + 14, py);
    context.scale(-1, 1);
  } else {
    context.translate(px, py);
  }

  if (pose === 'death') {
    context.fillStyle = '#d82800';
    context.fillRect(3, 1, 8, 3);
    context.fillStyle = '#f8b800';
    context.fillRect(4, 4, 7, 5);
    context.fillStyle = '#000000';
    context.fillRect(9, 5, 2, 2);
    context.fillStyle = '#f8f8f8';
    context.fillRect(2, 9, 10, 3);
    context.restore();
    return;
  }

  const stride = pose === 'run' && frame % 2 === 1 ? 2 : 0;
  const airborne = pose === 'jump' || pose === 'fall';
  context.fillStyle = '#d82800';
  context.fillRect(3, 0, 7, 2);
  context.fillRect(2, 2, 10, 2);
  context.fillStyle = '#f8b800';
  context.fillRect(3, 4, 8, 4);
  context.fillStyle = '#6b2700';
  context.fillRect(2, 4, 2, 3);
  context.fillRect(9, 5, 3, 1);
  context.fillStyle = '#000000';
  context.fillRect(8, 4, 1, 2);
  context.fillStyle = '#d82800';
  context.fillRect(3, 8, 8, 3);
  context.fillStyle = '#0050b8';
  context.fillRect(4, 10, 6, 4);
  context.fillStyle = '#f8b800';
  context.fillRect(1, airborne ? 8 : 9, 3, 3);
  if (airborne) {
    context.fillStyle = '#6b2700';
    context.fillRect(2, 13, 5, 2);
    context.fillRect(9, 11, 4, 2);
  } else {
    context.fillStyle = '#6b2700';
    context.fillRect(2 + stride, 14, 5, 2);
    context.fillRect(8 - stride, 14, 5, 2);
  }
  context.restore();
}

export function drawFallbackGoomba(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  frame: number,
  squashed: boolean,
): void {
  const px = Math.round(x);
  const py = Math.round(y);
  context.fillStyle = '#000000';
  if (squashed) {
    context.fillRect(px + 1, py + 8, 14, 5);
    context.fillStyle = '#a84800';
    context.fillRect(px + 2, py + 7, 12, 4);
    context.fillStyle = '#f8d878';
    context.fillRect(px + 5, py + 8, 2, 2);
    context.fillRect(px + 9, py + 8, 2, 2);
    return;
  }
  context.fillRect(px + 3, py + 1, 10, 2);
  context.fillRect(px + 1, py + 3, 14, 8);
  context.fillStyle = '#a84800';
  context.fillRect(px + 4, py + 1, 8, 2);
  context.fillRect(px + 2, py + 3, 12, 7);
  context.fillStyle = '#f8d878';
  context.fillRect(px + 4, py + 4, 3, 4);
  context.fillRect(px + 9, py + 4, 3, 4);
  context.fillStyle = '#000000';
  context.fillRect(px + 5, py + 4, 1, 2);
  context.fillRect(px + 10, py + 4, 1, 2);
  context.fillRect(px + 6, py + 9, 4, 2);
  const step = frame % 2;
  context.fillRect(px + (step ? 1 : 2), py + 11, 6, 3);
  context.fillRect(px + (step ? 9 : 8), py + 11, 6, 3);
}

export function drawFallbackTile(
  context: CanvasRenderingContext2D,
  tile: TileKind,
  x: number,
  y: number,
): void {
  const px = Math.round(x);
  const py = Math.round(y);
  if (tile === 'ground-top' || tile === 'ground-fill') {
    context.fillStyle = '#000000';
    context.fillRect(px, py, 16, 16);
    context.fillStyle = '#d87828';
    context.fillRect(px + 1, py + 1, 14, 14);
    context.fillStyle = '#f8b878';
    context.fillRect(px + 2, py + 2, 6, 3);
    context.fillRect(px + 9, py + 7, 5, 3);
    context.fillStyle = '#904010';
    context.fillRect(px + 7, py + 5, 2, 10);
    context.fillRect(px + 1, py + 10, 6, 2);
    if (tile === 'ground-top') {
      context.fillStyle = '#f8d878';
      context.fillRect(px + 1, py + 1, 14, 2);
    }
    return;
  }

  context.fillStyle = '#000000';
  context.fillRect(px, py, 16, 16);
  context.fillStyle = tile === 'stair-block' ? '#b85018' : '#c84c0c';
  context.fillRect(px + 1, py + 1, 14, 14);
  context.fillStyle = '#f8b878';
  context.fillRect(px + 1, py + 1, 14, 2);
  context.fillRect(px + 1, py + 8, 14, 2);
  context.fillStyle = '#6b2700';
  context.fillRect(px + 7, py + 2, 2, 6);
  context.fillRect(px + 4, py + 9, 2, 6);
  context.fillRect(px + 12, py + 9, 2, 6);
}

export function drawFallbackBlock(
  context: CanvasRenderingContext2D,
  kind: BlockKind | 'used',
  x: number,
  y: number,
  frame: number,
): void {
  const px = Math.round(x);
  const py = Math.round(y);
  if (kind === 'brick') {
    drawFallbackTile(context, 'brick-platform', px, py);
    return;
  }
  context.fillStyle = '#000000';
  context.fillRect(px, py, 16, 16);
  context.fillStyle = kind === 'used' ? '#a86820' : frame % 2 === 0 ? '#f8b800' : '#f8a000';
  context.fillRect(px + 1, py + 1, 14, 14);
  context.fillStyle = kind === 'used' ? '#d89848' : '#fff0a0';
  context.fillRect(px + 2, py + 2, 12, 2);
  if (kind !== 'used') {
    context.fillStyle = '#6b2700';
    context.fillRect(px + 6, py + 4, 5, 2);
    context.fillRect(px + 9, py + 6, 2, 3);
    context.fillRect(px + 7, py + 9, 2, 2);
    context.fillRect(px + 7, py + 12, 2, 2);
  }
}

export function drawFallbackPipe(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  const px = Math.round(x);
  const py = Math.round(y);
  context.fillStyle = '#000000';
  context.fillRect(px, py, width, height);
  context.fillStyle = '#00a800';
  context.fillRect(px + 2, py + 2, width - 4, height - 2);
  context.fillStyle = '#58d854';
  context.fillRect(px + 5, py + 2, 5, height - 3);
  context.fillStyle = '#007800';
  context.fillRect(px + width - 7, py + 2, 5, height - 3);
  context.fillStyle = '#00d800';
  context.fillRect(px - 3, py, width + 6, 7);
  context.fillStyle = '#58f858';
  context.fillRect(px + 3, py + 1, 6, 5);
  context.strokeStyle = '#000000';
  context.lineWidth = 1;
  context.strokeRect(px - 3.5, py + 0.5, width + 7, 7);
}

export function drawFallbackCoin(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  frame: number,
): void {
  const width = frame % 4 === 1 || frame % 4 === 3 ? 4 : 8;
  const px = Math.round(x + (8 - width) / 2);
  const py = Math.round(y);
  context.fillStyle = '#6b2700';
  context.fillRect(px, py, width, 12);
  context.fillStyle = '#f8d800';
  context.fillRect(px + 1, py + 1, Math.max(1, width - 2), 10);
  if (width > 4) {
    context.fillStyle = '#fff0a0';
    context.fillRect(px + 2, py + 2, 2, 7);
  }
}

export function drawFallbackScenery(
  context: CanvasRenderingContext2D,
  kind: ScenicKind,
  x: number,
  y: number,
  variant: number,
): void {
  const px = Math.round(x);
  const py = Math.round(y);
  if (kind === 'cloud') {
    context.fillStyle = '#000000';
    context.fillRect(px + 8, py + 3, 24 + variant * 8, 12);
    context.fillRect(px + 2, py + 8, 38 + variant * 8, 10);
    context.fillStyle = '#ffffff';
    context.fillRect(px + 9, py + 2, 22 + variant * 8, 12);
    context.fillRect(px + 3, py + 7, 36 + variant * 8, 10);
    context.fillStyle = '#78b8f8';
    context.fillRect(px + 7, py + 14, 28 + variant * 8, 2);
    return;
  }
  if (kind === 'hill') {
    const width = 42 + variant * 12;
    context.fillStyle = '#000000';
    for (let row = 0; row < 30; row += 2) {
      const inset = Math.floor((30 - row) * width / 62);
      context.fillRect(px + inset, py + row, width - inset * 2, 3);
    }
    context.fillStyle = '#38b838';
    for (let row = 2; row < 30; row += 2) {
      const inset = Math.floor((30 - row) * width / 62) + 1;
      context.fillRect(px + inset, py + row, width - inset * 2 - 2, 3);
    }
    context.fillStyle = '#006800';
    context.fillRect(px + Math.floor(width * 0.45), py + 16, 2, 5);
    context.fillRect(px + Math.floor(width * 0.58), py + 16, 2, 5);
    return;
  }
  if (kind === 'bush') {
    context.fillStyle = '#000000';
    context.fillRect(px, py + 6, 42 + variant * 14, 15);
    context.fillStyle = '#00a800';
    context.fillRect(px + 1, py + 7, 40 + variant * 14, 13);
    context.fillRect(px + 6, py + 2, 14, 12);
    context.fillRect(px + 22, py + (variant ? 0 : 4), 15, 14);
    context.fillStyle = '#58d854';
    context.fillRect(px + 7, py + 5, 6, 4);
    return;
  }
  if (kind === 'flagpole') {
    context.fillStyle = '#ffffff';
    context.fillRect(px + 7, py, 2, 96);
    context.fillStyle = '#38b838';
    context.fillRect(px + 1, py + 8, 14, 10);
    context.fillStyle = '#000000';
    context.fillRect(px + 8, py, 3, 3);
    return;
  }
  context.fillStyle = '#000000';
  context.fillRect(px, py + 12, 48, 36);
  context.fillRect(px + 5, py, 12, 20);
  context.fillRect(px + 31, py, 12, 20);
  context.fillStyle = variant === 0 ? '#c84c0c' : '#a84008';
  context.fillRect(px + 2, py + 13, 44, 33);
  context.fillRect(px + 7, py + 2, 8, 18);
  context.fillRect(px + 33, py + 2, 8, 18);
  context.fillStyle = '#000000';
  context.fillRect(px + 20, py + 31, 10, 15);
  context.fillRect(px + 9, py + 20, 5, 7);
  context.fillRect(px + 34, py + 20, 5, 7);
}
